// changed by Cursor: reusable component untuk render recap dengan annotations
import React, { useMemo, useRef, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { Flag, Check } from "phosphor-react";
import DarkModeToggle from "../../DarkModeToggle";
import ReportNoteModal from "../../LanguageLearning/ReportNoteModal";
import { reportedNoteState } from "../../../../recoil/atoms";

// Build annotations map from annotations array
const buildAnnotationsMap = (annotations = []) => {
  const map = annotations.reduce((acc, annotation) => {
    const annId = annotation?.["ann-id"] || annotation?.annId || annotation?.id;
    const streamUrl =
      annotation?.["stream-url"] ||
      annotation?.streamUrl ||
      annotation?.stream_url;
    const annType =
      annotation?.["ann-type"] || annotation?.annType || annotation?.type;
    const text = annotation?.text || "";

    // Accept audio type or if no type specified (for backward compatibility)
    // if (!annId) {
    //   console.warn("RecapSection: Annotation missing ann-id:", annotation);
    //   return acc;
    // }

    // if (!streamUrl) {
    //   console.warn(
    //     `RecapSection: Annotation ${annId} missing stream-url:`,
    //     annotation
    //   );
    //   return acc;
    // }

    // if (annType && annType !== "audio") {
    //   console.warn(
    //     `RecapSection: Annotation ${annId} has unsupported type: ${annType}`
    //   );
    //   return acc;
    // }

    acc[annId] = {
      ...annotation,
      annId,
      streamUrl,
      annType: annType || "audio",
      text,
    };
    return acc;
  }, {});

  // console.log("🔍 RecapSection - buildAnnotationsMap result:", {
  //   inputCount: annotations.length,
  //   outputCount: Object.keys(map).length,
  //   mapKeys: Object.keys(map),
  //   map: map,
  // });

  return map;
};

// Enhance HTML content with annotation metadata
const enhanceContentWithAnnotations = (html = "", annotationsMap) => {
  if (!html) {
    return html;
  }

  if (
    typeof window === "undefined" ||
    typeof window.DOMParser === "undefined"
  ) {
    return html;
  }

  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Find all nodes with ann-id or data-ann-id attributes
    const nodes = doc.querySelectorAll("[ann-id], [data-ann-id]");

    // console.log("🔍 RecapSection - enhanceContentWithAnnotations:", {
    //   htmlLength: html.length,
    //   nodesFound: nodes.length,
    //   annotationsMapSize: annotationsMap
    //     ? Object.keys(annotationsMap).length
    //     : 0,
    //   annotationsMapKeys: annotationsMap ? Object.keys(annotationsMap) : [],
    // });

    nodes.forEach((node) => {
      // Get ann-id from either attribute
      const annId =
        node.getAttribute("ann-id") || node.getAttribute("data-ann-id");

      if (!annId) {
        return;
      }

      // Check if this ann-id exists in annotationsMap
      const annotationData = annotationsMap && annotationsMap[annId];
      // if (!annotationData || !annotationData.streamUrl) {
      //   // If no matching annotation data, skip enhancement
      //   console.warn(`RecapSection: No annotation data found for ann-id: ${annId}`, {
      //     annId,
      //     hasAnnotationsMap: !!annotationsMap,
      //     annotationsMapKeys: annotationsMap ? Object.keys(annotationsMap) : [],
      //   });
      //   return;
      // }

      // console.log(`🔍 RecapSection - Enhancing node with ann-id: ${annId}`, {
      //   annId,
      //   streamUrl: annotationData.streamUrl,
      // });

      // Ensure data-ann-id is set (standardize to data-ann-id)
      node.setAttribute("data-ann-id", annId);

      // Remove ann-id if it exists (keep only data-ann-id)
      if (node.hasAttribute("ann-id")) {
        node.removeAttribute("ann-id");
      }

      // Preserve existing annotation classes (annotation, annotation-)
      // Add our styling class
      if (!node.classList.contains("review-recap-annotation")) {
        node.classList.add("review-recap-annotation");
      }

      // Add accessibility attributes
      if (!node.hasAttribute("tabindex")) {
        node.setAttribute("tabindex", "0");
      }

      if (!node.hasAttribute("role")) {
        node.setAttribute("role", "button");
      }

      const annotationText =
        node.textContent?.trim() || annotationData.text || "this word";
      node.setAttribute("aria-label", `Play audio for ${annotationText}`);
    });

    return doc.body.innerHTML;
  } catch (error) {
    // console.warn(
    //   "RecapSection: failed to enhance content with annotations",
    //   error
    // );
    return html;
  }
};

// Utility to safely find annotation trigger target
const findAnnotationTrigger = (event) => {
  const target = event.target;
  if (target && typeof target.closest === "function") {
    return target.closest("[data-ann-id]");
  }

  const parentElement = target?.parentElement;
  if (parentElement && typeof parentElement.closest === "function") {
    return parentElement.closest("[data-ann-id]");
  }

  return null;
};

const RecapSection = ({ recap, annotations = [], question = null }) => {
  const audioRef = useRef(null);
  const activeAnnotationRef = useRef(null);
  const [reportNoteId, setReportNoteId] = useState(null);
  const [reportedNoteSet, setReportedNoteSet] = useRecoilState(reportedNoteState);

  const noteId = useMemo(() => {
    const originalQuestion = question?.originalQuestion || {};
    return (
      originalQuestion?.["note-id"] ||
      originalQuestion?.noteId ||
      originalQuestion?.note_id ||
      question?.["note-id"] ||
      question?.noteId ||
      question?.note_id ||
      null
    );
  }, [question]);

  const reportNoteKey = useMemo(() => {
    if (!noteId) return null;
    return `reported-note-${noteId}`;
  }, [noteId]);

  const isReportedLocal = useMemo(() => {
    if (!reportNoteKey) return false;
    return reportedNoteSet.has(reportNoteKey);
  }, [reportNoteKey, reportedNoteSet]);

  // Build annotations map
  const annotationsMap = useMemo(() => {
    const map = buildAnnotationsMap(annotations);
    // console.log("🔍 RecapSection - Annotations map:", {
    //   annotationsCount: annotations.length,
    //   mapSize: Object.keys(map).length,
    //   mapKeys: Object.keys(map),
    //   annotations: annotations,
    // });
    return map;
  }, [annotations]);

  // Enhance recap HTML with annotations
  const enhancedRecap = useMemo(() => {
    if (!recap) return "";
    const enhanced = enhanceContentWithAnnotations(recap, annotationsMap);
    // console.log("🔍 RecapSection - Enhanced recap:", {
    //   hasRecap: !!recap,
    //   recapLength: recap.length,
    //   recapPreview: recap.substring(0, 100),
    //   hasAnnotations: Object.keys(annotationsMap).length > 0,
    //   enhancedLength: enhanced.length,
    //   enhancedPreview: enhanced.substring(0, 100),
    // });
    return enhanced;
  }, [recap, annotationsMap]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (
      activeAnnotationRef.current &&
      activeAnnotationRef.current !== element
    ) {
      activeAnnotationRef.current.classList.remove(
        "review-recap-annotation-active"
      );
    }

    activeAnnotationRef.current = element;
    element.classList.add("review-recap-annotation-active");
  };

  const playAnnotationAudio = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) {
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener("ended", () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove(
            "review-recap-annotation-active"
          );
          activeAnnotationRef.current = null;
        }
      });
    }

    const audioEl = audioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl.play().catch((error) => {
      console.warn("RecapSection: failed to play annotation audio", error);
    });
  };

  const handleAnnotationTrigger = (event) => {
    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute("data-ann-id");
    if (!annId) {
      return;
    }

    if (!annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    playAnnotationAudio(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const hasAnnotations =
    annotationsMap && Object.keys(annotationsMap).length > 0;

  // Don't render if no recap
  if (!recap) {
    return null;
  }

  return (
    <div className="review-recap-section mt-0 p-5 bg-[#f0f9ff] dark:bg-[rgba(59,130,246,0.1)] rounded-lg border-none max-md:py-4 max-md:px-5">
      <div className="review-recap-header flex items-center justify-between mb-0">
        <h4 className="text-base font-bold mb-0 text-[#d97706] dark:text-[#2dd4bf] flex items-center">💡 Recap</h4>
        <div className="flex items-center gap-2.5">
          {noteId && (
            <button
              type="button"
              className={`review-recap-report-note-btn inline-flex items-center gap-1.5 py-1.5 px-3 rounded-xl text-[13px] font-semibold leading-none cursor-pointer transition-all duration-200 ${
                isReportedLocal
                  ? "bg-[#f0fdf4] dark:bg-[rgba(34,197,94,0.1)] border border-[#86efac] dark:border-[rgba(134,239,172,0.3)] text-[#16a34a] dark:text-[#4ade80] hover:bg-[#dcfce7] dark:hover:bg-[rgba(34,197,94,0.15)] hover:border-[#4ade80]"
                  : "bg-[rgba(18,118,109,0.1)] dark:bg-[rgba(45,212,191,0.12)] border border-[rgba(18,118,109,0.24)] dark:border-[rgba(45,212,191,0.25)] text-[#146e69] dark:text-[#2dd4bf] hover:bg-[rgba(18,118,109,0.2)] dark:hover:bg-[rgba(45,212,191,0.2)] hover:border-[rgba(18,118,109,0.45)] dark:hover:border-[rgba(45,212,191,0.45)] hover:scale-[1.02]"
              }`}
              onClick={() => setReportNoteId(noteId)}
              aria-label={isReportedLocal ? "Reported" : "Report this note"}
            >
              {isReportedLocal ? (
                <>
                  <Check size={16} weight="regular" />
                  <span>Reported</span>
                </>
              ) : (
                <>
                  <Flag size={16} weight="regular" />
                  <span>Report this note</span>
                </>
              )}
            </button>
          )}
          <DarkModeToggle variant="recap" />
        </div>
      </div>
      <div
        className="review-recap-content text-[15px] leading-[1.7] text-[var(--text-primary)] w-full max-w-full"
        onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
        onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
        dangerouslySetInnerHTML={{ __html: enhancedRecap }}
      />
      <ReportNoteModal
        noteId={reportNoteId}
        isOpen={reportNoteId !== null}
        onClose={() => setReportNoteId(null)}
        onSuccess={() => {
          if (!reportNoteKey) return;
          setReportedNoteSet((prev) => new Set([...prev, reportNoteKey]));
        }}
      />
    </div>
  );
};

export default RecapSection;
